import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import Settings from './Settings';
import { DEFAULT_SYSTEM_PROMPT } from "../constants/system_prompt_constant"

const DEFAULT_API_URL = process.env.REACT_APP_API_URL;
const STORAGE_KEY_SYSTEM_PROMPT = 'bidding-agent-system-prompt';

const ChatUI = () => {
  const WELCOME_MESSAGE = {
    id: 1,
    text: "Hello! How can I help you today?",
    isBot: true,
    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  };

  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [apiUrl, setApiUrl] = useState(DEFAULT_API_URL);
  const [systemPrompt, setSystemPrompt] = useState(() => {
    // Load from localStorage if available
    const savedPrompt = localStorage.getItem(STORAGE_KEY_SYSTEM_PROMPT);
    return savedPrompt || DEFAULT_SYSTEM_PROMPT;
  });
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Add refs for message content
  const messageContentRefs = useRef({});

  // Save system prompt to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_SYSTEM_PROMPT, systemPrompt);
  }, [systemPrompt]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [inputMessage]);

  const handleClearChat = () => {
    setMessages([{
      ...WELCOME_MESSAGE,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }]);
  };

  // Function to copy message text
  const copyMessageText = (messageId) => {
    const messageElement = messageContentRefs.current[messageId];
    if (messageElement) {
      // Create a temporary div to hold the content
      const tempDiv = document.createElement('div');
      
      // Clone the message content (excluding timestamp)
      const contentToClone = messageElement.cloneNode(true);
      
      // Remove the timestamp element
      const timestamp = contentToClone.querySelector('.message-timestamp');
      if (timestamp) {
        timestamp.remove();
      }
      
      tempDiv.appendChild(contentToClone);
      
      // Get all links and ensure they have the proper format for copying
      const links = tempDiv.querySelectorAll('a');
      links.forEach(link => {
        // If the link text doesn't include the URL, append it
        if (link.textContent !== link.href && !link.textContent.includes(link.href)) {
          link.textContent = `${link.textContent} (${link.href})`;
        }
      });
      
      // Get the text content from the modified element
      const textToCopy = tempDiv.textContent.trim();
      
      navigator.clipboard.writeText(textToCopy)
        .then(() => {
          // Optional: Show a temporary success indicator
          const copyButton = document.querySelector(`#copy-button-${messageId}`);
          if (copyButton) {
            const originalText = copyButton.innerHTML;
            copyButton.innerHTML = '✓';
            setTimeout(() => {
              copyButton.innerHTML = originalText;
            }, 1500);
          }
        })
        .catch(err => {
          console.error('Failed to copy text: ', err);
        });
    }
  };

  const fetchBotResponse = async (userQuery) => {
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          message: userQuery,
          systemPrompt: systemPrompt 
        })
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      return data.message.content;
    } catch (error) {
      console.error('Error:', error);
      return "I apologize, but I'm having trouble connecting right now. Please try again later.";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (inputMessage.trim() === '' || isLoading) return;

    const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    // Add user message
    const newMessage = {
      id: messages.length + 1,
      text: inputMessage,
      isBot: false,
      timestamp: currentTime
    };

    setMessages(prev => [...prev, newMessage]);
    setInputMessage('');
    setIsLoading(true);

    // Get bot response
    const botResponse = await fetchBotResponse(inputMessage);
    
    setMessages(prev => [...prev, {
      id: prev.length + 1,
      text: botResponse,
      isBot: true,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }]);
    
    setIsLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <h2>Chat Assistant</h2>
        <div className="header-buttons">
          <button onClick={() => setIsSettingsOpen(true)} className="settings-button">
            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none">
              <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
            </svg>
          </button>
          <button onClick={handleClearChat} className="clear-chat-button">
            Clear Chat
          </button>
        </div>
      </div>
      <div className="chat-messages">
        {messages.map(message => (
          <div
            key={message.id}
            className={`message ${message.isBot ? 'bot' : 'user'}`}
          >
            <div className="message-content">
              <div 
                ref={el => messageContentRefs.current[message.id] = el}
                className="message-text"
              >
                {message.isBot ? (
                  <ReactMarkdown>{message.text}</ReactMarkdown>
                ) : (
                  message.text
                )}
                <span className="message-timestamp">{message.timestamp}</span>
              </div>
              {message.isBot && (
                <button 
                  id={`copy-button-${message.id}`}
                  className="copy-button"
                  onClick={() => copyMessageText(message.id)}
                  title="Copy message"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path>
                  </svg>
                </button>
              )}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="message bot loading">
            <div className="loading-indicator">
              <div className="loading-spinner"></div>
              <span>AI is thinking...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <form onSubmit={handleSubmit} className="chat-input-form">
        <textarea
          ref={textareaRef}
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your message here..."
          className="chat-input"
          disabled={isLoading}
          rows="1"
        />
        <button 
          type="submit" 
          className="send-button"
          disabled={inputMessage.trim() === '' || isLoading}
        >
          Send
        </button>
      </form>
      <Settings
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        defaultApiUrl={apiUrl}
        onApiUrlChange={setApiUrl}
        defaultSystemPrompt={systemPrompt}
        onSystemPromptChange={setSystemPrompt}
      />
    </div>
  );
};

export default ChatUI;